import * as Sentry from '@sentry/browser'

/** Stable empty ancestry for browse-mode callers, where nothing can be covered. */
export const kNoSelectionAncestry: ReadonlyMap<string, readonly string[]> = new Map()

export type PruneCoveredSelectionParams = {
  fileIds: readonly string[]
  folderIds: readonly string[]
  /** Ancestor folder ids per selected item id, from each search result row's `folder_path`. */
  ancestorFolderIdsById: ReadonlyMap<string, readonly string[]>
}

/**
 * Drops any selected file or folder whose ancestor folder is also selected, so a selection that
 * spans folders (search results) cannot ask the backend to move or delete an item and its own
 * container in separate batched requests.
 *
 * Without this, `buildSelectionBatchesFoldersFirstThenFiles` splits folders and files into separate
 * requests, so the backend's within-request descendant pruning never applies: a move would carry a
 * file along with its folder and then pull it back out into the destination on the next request.
 *
 * Passing an empty map is the identity — browse-mode selections are single-folder by construction
 * (`useDriveSelection` resets when the folder changes), so nothing is ever covered there.
 */
export function pruneCoveredSelection({ fileIds, folderIds, ancestorFolderIdsById }: PruneCoveredSelectionParams): { fileIds: string[]; folderIds: string[] } {
  if (ancestorFolderIdsById.size === 0) {
    return { fileIds: [...fileIds], folderIds: [...folderIds] }
  }

  const selectedFolderIds = new Set(folderIds)
  const missingAncestryIds: string[] = []

  const isCovered = (id: string): boolean => {
    const ancestorIds = ancestorFolderIdsById.get(id)
    if (!ancestorIds) {
      // Keeping the item preserves today's behaviour; silently dropping something the user ticked
      // is worse. Ancestry is always present in search results, so a miss is a programming error.
      missingAncestryIds.push(id)
      return false
    }
    return ancestorIds.some((ancestorId) => selectedFolderIds.has(ancestorId))
  }

  const prunedFileIds = fileIds.filter((fileId) => !isCovered(fileId))
  const prunedFolderIds = folderIds.filter((folderId) => !isCovered(folderId))

  if (missingAncestryIds.length > 0) {
    Sentry.addBreadcrumb({
      category: 'user_files',
      level: 'warning',
      message: 'Selection pruning found no ancestry for selected items',
      data: { missingAncestryIds },
    })
  }

  return { fileIds: prunedFileIds, folderIds: prunedFolderIds }
}
